// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import color from "@awc/XDL/Utilities/color";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const generalStyles = css`
    & + label {
        margin-left: 0;
        margin-right: 8px;
        align-items: flex-start;
    }
    & + label::before {
        flex: 1 0 13px;
        width: 13px;
        height: 13px;
        border-color: ${color("common.ui4")};
        background: ${color("common.ui1")};
        margin-right: 4px;
        margin-top: 0;
    }
    &:checked + label::after {
        width: 13px;
        height: 13px;
        left: 0;
        top: 0px;
    }
    &:focus + label::before {
        box-shadow: none;
    }
`;

const hoverStyle = css`
    &:hover:checked:not(:disabled) + label::before {
        border-color: transparent;
    }

    &:hover:not(:disabled) + label::before {
        border-color: ${color("common.ui8")};
    }

    &:hover:not(:disabled) + label {
        cursor: pointer;
    }

    &:hover:checked:not(:disabled) + label::after {
        border: 1px solid ${color("common.ui8")};
    }
`;

const checkedStyle = css`
    &:checked:not(:disabled) + label::before {
        border-color: transparent;
        background: transparent;
    }

    &:checked + label::after {
        background: ${color("common.ui9")};
        outline: 1px solid ${color("common.ui1")};
        outline-offset: -2px;
        border: 1px solid ${color("common.ui4")};
    }
`;

const disabledStyle = css`
    &:disabled + label {
        opacity: 1;
    }
    &:disabled + label::before {
        opacity: 0.33;
    }
    &:disabled + label::after {
        opacity: 0.33;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: fontConfig.fontSize.xSmall,
            weight: fontConfig.weight.regular,
            color: "primary.main",
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLinexSmall};
                user-select: none;
            `,
        },
        css: css`
            input {
                margin-left: 0px;
                margin-right: 0px;
                margin-top: 0px;
                width: 13px;
                height: 13px;
                display: none;
                ${generalStyles}
                ${hoverStyle}
                ${checkedStyle}
                ${disabledStyle}
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        labelProps: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.medium,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                margin-bottom: 12px;
                &:empty {
                    display: none;
                }
            }
            ${(props: any) => {
                return props.horizontal
                    ? css`
                          & > ${TypographyStyle as any} {
                              margin-bottom: 4px;
                          }
                          display: inline-block;
                          & > ${RadioStyle} {
                              display: inline-block;
                              margin-top: 0px;
                              margin-right: 16px;
                          }
                      `
                    : css`
                          display: flex;
                          flex-direction: column;
                          ${RadioStyle} {
                              display: flex;
                          }
                          ${RadioStyle} + ${RadioStyle} {
                              margin-top: 12px;
                          }
                      `;
            }}
        `,
    },
} as xBaseTheme["radio"];
